import { config } from '../config';
import { lotMetaStore, type LotMeta, type LotStatus } from './plots';

const KNOWN_STATUSES: ReadonlySet<string> = new Set([
  'available',
  'under-contract',
  'pending',
  'sold',
]);

function parseStatus(s: string | undefined): LotStatus | undefined {
  if (!s) return undefined;
  const norm = s.trim().toLowerCase().replace(/\s+/g, '-');
  return KNOWN_STATUSES.has(norm) ? (norm as LotStatus) : undefined;
}

// Bump this whenever the parse logic changes shape so old cached entries
// get discarded instead of feeding stale fields into the store.
const STORAGE_KEY = 'manglillo.lotMeta.v2';

function readLocalCache(): Array<[string, LotMeta]> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Array<[string, LotMeta]>;
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch {
    return null;
  }
}

function writeLocalCache(entries: Array<[string, LotMeta]>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // private mode / quota — ignore, the SW cache still has it for next time
  }
}

/**
 * Populate `lotMetaStore` with per-lot dimensions:
 *   1. Hydrate synchronously from localStorage (~2.4 KB JSON) so the lot
 *      panel has data on the very first paint, even on a cold offline
 *      launch where the user never opened any lot online before.
 *   2. In the background, fetch the published Google Sheet CSV and update
 *      both the store and the localStorage cache so future loads are
 *      current.
 *
 * Silent on any failure — the UI falls back to polygon-computed values
 * for fields the CSV would normally supply.
 */
export async function loadLotMeta(): Promise<void> {
  const cached = readLocalCache();
  if (cached) lotMetaStore.setAll(cached);
  try {
    const res = await fetch(config.lotsMetaCsvUrl);
    if (!res.ok) return;
    const rows = parseCsv(await res.text());
    const entries: Array<[string, LotMeta]> = [];
    for (const row of rows) {
      const id = row['ID']?.trim();
      if (!id) continue;
      const meta: LotMeta = {
        id,
        lot: row['Lot']?.trim() || undefined,
        ancho: parseNum(row['Ancho']),
        oeste: parseNum(row['Oeste']),
        este:  parseNum(row['Este']),
        area:  parseNum(row['Area']),
        status: parseStatus(row['Status']),
      };
      entries.push([id, meta]);
    }
    if (entries.length > 0) {
      lotMetaStore.setAll(entries);
      writeLocalCache(entries);
    }
  } catch {
    // network / CORS / parse error — fall back to whatever we hydrated
    // from localStorage (or polygon-computed values if neither exists)
  }
}

function parseNum(s: string | undefined): number | undefined {
  if (s === undefined) return undefined;
  let cleaned = s.trim();
  if (cleaned === '') return undefined;
  // The sheet mixes conventions: "11,353.55" uses comma as a thousands
  // separator, while "59,75" / "127,77" use comma as the DECIMAL separator.
  // Only strip commas when a dot is also present (true thousands sep);
  // otherwise treat the lone comma as a decimal point.
  if (cleaned.includes(',') && cleaned.includes('.')) cleaned = cleaned.replace(/,/g, '');
  else if (cleaned.includes(',')) cleaned = cleaned.replace(',', '.');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : undefined;
}

/** Proper CSV parser — handles quoted fields with embedded commas. */
function parseCsv(text: string): Array<Record<string, string>> {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  while (lines.length && lines[lines.length - 1] === '') lines.pop();
  if (lines.length === 0) return [];
  const header = parseRow(lines[0]);
  const out: Array<Record<string, string>> = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = parseRow(lines[i]);
    const obj: Record<string, string> = {};
    header.forEach((h, idx) => {
      obj[h] = cols[idx] ?? '';
    });
    out.push(obj);
  }
  return out;
}

function parseRow(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (c === '"') {
        inQuotes = false;
      } else {
        cur += c;
      }
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ',') {
        out.push(cur);
        cur = '';
      } else cur += c;
    }
  }
  out.push(cur);
  return out;
}
